import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import Layout from '../../../components/feature/Layout';
import PageBanner from '../../../components/feature/PageBanner';
import { Link } from 'react-router-dom';

const services = [
  { icon: 'ri-building-4-line', title: 'New Construction Management', desc: 'We manage the full process of new residential builds — from breaking ground to final certificate of occupancy.' },
  { icon: 'ri-tools-line', title: 'Subcontractor Coordination', desc: 'We coordinate licensed electricians, plumbers, HVAC techs, and other trades so every phase stays on schedule.' },
  { icon: 'ri-file-list-2-line', title: 'Permit & Code Compliance', desc: 'All permits pulled, all inspections scheduled. We ensure every project meets current Michigan building codes.' },
  { icon: 'ri-calendar-check-line', title: 'Project Scheduling', desc: 'Detailed schedules kept and updated weekly — you always know exactly where your project stands.' },
  { icon: 'ri-money-dollar-circle-line', title: 'Budget Management', desc: 'Transparent line-item budgeting and change-order management with zero surprises. We watch every dollar for you.' },
  { icon: 'ri-shield-check-line', title: 'Quality Inspections', desc: 'Rigorous in-process and final quality checks ensure the finished product meets our high standards — every time.' },
];

const projectTypes = [
  'Custom Home Builds', 'Residential Remodels', 'Commercial Light Construction',
  'Accessory Dwelling Units', 'Property Improvements', 'Multi-Trade Projects',
  'Storm Damage Reconstruction', 'Historic Renovations',
];

export default function GeneralContractingPage() {
  const { ref: svcRef, visible: svcVis } = useScrollAnimation();
  const { ref: ptRef, visible: ptVis } = useScrollAnimation();

  return (
    <Layout>
      <PageBanner
        title="General Contracting"
        subtitle="One point of contact for your entire construction project. We handle every trade, every permit, every timeline — so you don't have to."
        breadcrumb="General Contracting"
        imageUrl="https://public.readdy.ai/ai/img_res/fc8c8eff6bb6d0fa7b10fdd969c78aad.jpg"
        accentWord="Contracting"
      />

      {/* Intro */}
      <section className="bg-[#fafaf8] py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-0.5 bg-primary" />
              <span className="text-primary text-xs font-bold uppercase tracking-[0.3em]">Why Choose Us</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase text-gray-900 leading-tight tracking-tight mb-5 sm:mb-6" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              One Call. <span className="text-primary">Everything Handled.</span>
            </h2>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-5">
              Managing a construction project involves dozens of moving parts — licensed trades, material deliveries, permit timelines, inspections. Most homeowners find it overwhelming. That's exactly why we exist.
            </p>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-7 sm:mb-8">
              As your general contractor, Downs Construction becomes your single point of accountability. We hire and manage all subcontractors, pull every permit, and keep the project on time and within budget. You get weekly updates and a finished product you're proud of.
            </p>
            <div className="flex flex-wrap gap-2 sm:gap-3 mb-7 sm:mb-8">
              {projectTypes.map((pt) => (
                <span key={pt} className="bg-primary-light border border-primary-border text-primary text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full hover:bg-primary hover:text-white transition-colors duration-200 cursor-default">
                  {pt}
                </span>
              ))}
            </div>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-bold uppercase tracking-widest text-sm px-7 py-4 rounded-md transition-colors duration-200 cursor-pointer whitespace-nowrap btn-press group">
              Get Started
              <i className="ri-arrow-right-line transition-transform duration-200 group-hover:translate-x-1"></i>
            </Link>
          </div>
          <div>
            <div className="relative">
              <div className="w-full h-[420px] sm:h-[480px] rounded-lg overflow-hidden img-zoom">
                <img src="https://readdy.ai/api/search-image?query=construction%20team%20meeting%20at%20residential%20job%20site%20with%20blueprints%20and%20plans%2C%20professional%20general%20contractor%20team%2C%20framed%20house%20in%20background%2C%20Michigan%20neighborhood%2C%20overcast%20sky%2C%20trucks%20and%20equipment%20visible&width=700&height=550&seq=gc02&orientation=portrait" alt="General contracting team" className="w-full h-full object-cover object-top" />
              </div>
              <div className="absolute top-5 -left-5 bg-gradient-to-br from-primary to-primary-mid text-white p-4 sm:p-5 rounded-lg">
                <div className="text-3xl sm:text-4xl font-black leading-none mb-1" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>1992</div>
                <div className="text-primary-light text-xs uppercase tracking-wider font-semibold">Founded</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="bg-primary-dark py-16 sm:py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 20% 80%, #2A5BA8 0%, transparent 50%)' }} />
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-16 relative z-10">
          <div className="text-center mb-12 sm:mb-14">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="w-8 h-0.5 bg-primary-light" />
              <span className="text-primary-light text-xs font-bold uppercase tracking-[0.3em]">What's Included</span>
              <div className="w-8 h-0.5 bg-primary-light" />
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase text-white tracking-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Full-Service <span className="text-primary-light">Contracting</span>
            </h2>
          </div>
          <div ref={svcRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {services.map((svc, i) => (
              <div
                key={svc.title}
                className="p-6 sm:p-7 bg-white/5 border border-white/10 rounded-lg hover-card hover:border-primary-mid/40"
                style={{ opacity: svcVis ? 1 : 0, transform: svcVis ? 'translateY(0)' : 'translateY(32px)', transition: `all 0.7s ease ${i * 100}ms` }}
              >
                <div className="w-12 h-12 flex items-center justify-center bg-primary/40 rounded-lg mb-5">
                  <i className={`${svc.icon} text-primary-light text-2xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-white uppercase tracking-wide mb-3" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{svc.title}</h3>
                <p className="text-blue-200/60 text-sm leading-relaxed">{svc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Downs */}
      <section className="bg-[#fafaf8] py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-16">
          <div ref={ptRef} className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 transition-all duration-700 ${ptVis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {[
              { icon: 'ri-award-line', title: 'Licensed & Insured', desc: 'Fully licensed Michigan contractor with comprehensive liability and workers\' compensation insurance on every job.' },
              { icon: 'ri-handshake-line', title: 'Honest Pricing', desc: 'Detailed written estimates, transparent change orders. No surprise invoices at the end of a project. Ever.' },
              { icon: 'ri-time-line', title: 'On-Time Delivery', desc: '29+ years of keeping projects on schedule. We respect your time and your life — construction delays aren\'t an option.' },
            ].map((item, i) => (
              <div key={item.title} className="text-center p-7 sm:p-8 bg-white border border-gray-100 rounded-lg hover-card" style={{ transitionDelay: `${i * 120}ms` }}>
                <div className="w-16 h-16 flex items-center justify-center bg-primary rounded-full mx-auto mb-5">
                  <i className={`${item.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 uppercase tracking-wide mb-3" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary to-primary-mid">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase text-white mb-4 tracking-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            Let's Build Something Great
          </h2>
          <p className="text-blue-100/80 mb-8 text-sm sm:text-base max-w-xl mx-auto">Tell us about your project and we'll put together a detailed plan and estimate — at no charge to you.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-primary font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-md hover:bg-primary-light transition-colors duration-200 cursor-pointer whitespace-nowrap btn-press">
              Free Consultation
            </Link>
            <a href="tel:269-599-4913" className="border-2 border-white/60 text-white font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-md hover:bg-white/10 transition-colors duration-200 cursor-pointer whitespace-nowrap btn-press">
              (269) 599-4913
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
